// #368: the month grid the calendar primitive draws. Built from local y/m/d
// parts only: `new Date("YYYY-MM-DD")` parses as UTC and shifts the day for a
// zone behind it. Keys: "YYYY-MM" for a month, "YYYY-MM-DD" for a day.
import { dateFormatter } from "./dateFormat.js";
import { assignedToSelfOnly } from "./schemas/plan.js";
import { formatLocalYmd } from "./timeRange.js";

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Local midnight of `ymd`, or null when it is not a real day. */
export const parseLocalYmd = (ymd: string): Date | null => {
  const m = YMD.exec(ymd);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const date = new Date(y, mo, d);
  return date.getFullYear() === y && date.getMonth() === mo && date.getDate() === d ? date : null;
};

/** "YYYY-MM" of a day key. */
export const monthKeyOf = (ymd: string): string => ymd.slice(0, 7);

const monthParts = (key: string): [number, number] => {
  const [y, m] = key.split("-").map(Number);
  return [y ?? 1970, (m ?? 1) - 1];
};

/** "YYYY-MM" moved by `n` months. */
export const shiftMonthKey = (key: string, n: number): string => {
  const [y, m] = monthParts(key);
  return monthKeyOf(formatLocalYmd(new Date(y, m + n, 1)));
};

/** 0 = Sunday, 1 = Monday. As CLDR has it: English (US) on Sunday, the
 *  other languages of the app on Monday. */
export const weekStartFor = (locale: string): 0 | 1 => (/^en(-us)?$/i.test(locale) ? 0 : 1);

/** The month's days as weeks of seven, from `weekStart`. null pads the days
 *  of the months before and after. */
export const monthWeeks = (key: string, weekStart: 0 | 1): (string | null)[][] => {
  const [y, m] = monthParts(key);
  const lead = (new Date(y, m, 1).getDay() - weekStart + 7) % 7;
  const days = new Date(y, m + 1, 0).getDate();
  const cells: (string | null)[] = Array.from({ length: lead }, () => null);
  for (let d = 1; d <= days; d += 1) cells.push(formatLocalYmd(new Date(y, m, d)));
  while (cells.length % 7 !== 0) cells.push(null);
  return Array.from({ length: cells.length / 7 }, (_, w) => cells.slice(w * 7, w * 7 + 7));
};

/** Weekday names in `locale`, from `weekStart`. "min": the short name's
 *  first two letters (Mo, Tu; lu, ma), a column of a narrow month. */
export const weekdayNames = (
  locale: string,
  weekStart: 0 | 1,
  style: "min" | "short" | "long" = "short",
): string[] => {
  const fmt = dateFormatter(locale, { weekday: style === "long" ? "long" : "short" });
  // 1 January 2023 was a Sunday.
  const names = Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2023, 0, 1 + ((i + weekStart) % 7))));
  return style === "min" ? names.map((n) => n.replace(/\.$/, "").slice(0, 2)) : names;
};

/** How many of `days` fall on each day: the calendar's markers. */
export const countByDay = (days: readonly (string | undefined)[]): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const d of days) if (d) out[d] = (out[d] ?? 0) + 1;
  return out;
};

/** The local day an instant falls on, "" when it is not one. Never
 *  `iso.slice(0, 10)`: that is the UTC day, the wrong one of an evening. */
export const localDayOf = (iso: string | undefined): string => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? formatLocalYmd(d) : "";
};

/** #374: sessions by the local day they started on, in their order. */
export const sessionsByDay = <T extends { startedAt?: string }>(
  sessions: readonly T[],
): Record<string, T[]> => {
  const out: Record<string, T[]> = {};
  for (const s of sessions) {
    const day = localDayOf(s.startedAt);
    if (day) (out[day] ??= []).push(s);
  }
  return out;
};

/** #374: the plans of a day before `today` not done: not marked completed
 *  or skipped (a skip is a choice, not a miss), and no session in
 *  `sessions` started from them. Since #374 the server marks a dated plan
 *  completed when a session saved from it lands, but `fromPlanId` stays the
 *  signal for two cases: rows older than that, and plans followed from
 *  another author (CO-37), whose row is the author's and is never marked.
 *  Only on days the loaded sessions cover (all the past once `allLoaded`,
 *  else after the oldest loaded day, which may go on in the next page):
 *  elsewhere «not done» is not known, so it is not said.
 *  #499: the reader `sub`'s own row of a plan assigned to others is what
 *  those athletes execute, not a plan of the reader's: never missed. A plan
 *  followed from another author (createdBy !== sub) still counts. `sub`
 *  null (not known yet): no plan is set aside. */
export const missedPlanIds = (
  plans: readonly {
    id: string;
    scheduledFor?: string;
    status: string;
    createdBy?: string;
    assignedTo?: readonly string[];
  }[],
  sessions: readonly { fromPlanId?: string; startedAt?: string }[],
  today: string,
  allLoaded: boolean,
  sub: string | null = null,
): Set<string> => {
  const started = new Set(sessions.flatMap((s) => (s.fromPlanId ? [s.fromPlanId] : [])));
  const days = sessions.map((s) => localDayOf(s.startedAt)).filter((d) => d !== "");
  const oldest = days.reduce((a, b) => (b < a ? b : a), today);
  const covered = (day: string) => allLoaded || (days.length > 0 && day > oldest);
  return new Set(
    plans
      .filter(
        (p) =>
          !!p.scheduledFor &&
          p.scheduledFor < today &&
          covered(p.scheduledFor) &&
          p.status !== "completed" &&
          p.status !== "skipped" &&
          !started.has(p.id) &&
          !(sub && p.createdBy === sub && !assignedToSelfOnly(p.assignedTo, sub)),
      )
      .map((p) => p.id),
  );
};
